import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { IdentityProvider, ProviderType, AttributeMapping } from "@/types/identity-provider";
import { toast } from "sonner";

interface ProviderConfigDialogProps {
  provider?: IdentityProvider | null;
  onSave: (provider: IdentityProvider) => void;
  onCancel: () => void;
}

export function ProviderConfigDialog({ provider, onSave, onCancel }: ProviderConfigDialogProps) {
  const [formData, setFormData] = useState<Partial<IdentityProvider>>({
    name: provider?.name || '',
    type: provider?.type || 'google',
    description: provider?.description || '',
    status: provider?.status || 'inactive',
    config: provider?.config || {},
    syncSettings: provider?.syncSettings || {
      syncUsers: true,
      syncGroups: true,
      syncInterval: 24,
      enableAutoSync: true,
      attributeMapping: {
        email: 'email',
        firstName: 'given_name',
        lastName: 'family_name'
      }
    }
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value
      }
    }));
  };

  const handleSyncSettingChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      syncSettings: {
        ...prev.syncSettings!,
        [field]: value
      }
    }));
  };

  const handleAttributeMappingChange = (attribute: string, mapping: string) => {
    setFormData(prev => ({
      ...prev,
      syncSettings: {
        ...prev.syncSettings!,
        attributeMapping: {
          ...prev.syncSettings!.attributeMapping,
          [attribute]: mapping
        }
      }
    }));
  };

  const handleSave = () => {
    if (!formData.name || !formData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    const savedProvider: IdentityProvider = {
      id: provider?.id || Date.now().toString(),
      name: formData.name,
      type: formData.type as ProviderType,
      status: formData.status || 'inactive',
      description: formData.description,
      config: formData.config || {},
      syncSettings: formData.syncSettings!,
      createdAt: provider?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(savedProvider);
    toast.success(`Identity provider ${provider ? 'updated' : 'created'} successfully`);
  };

  const renderProviderSpecificConfig = () => {
    switch (formData.type) {
      case 'google':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="googleDomain">Google Workspace Domain</Label>
              <Input
                id="googleDomain"
                placeholder="example.com"
                value={formData.config?.googleDomain || ''}
                onChange={(e) => handleConfigChange('googleDomain', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="googleClientId">Client ID</Label>
              <Input
                id="googleClientId"
                placeholder="Your Google OAuth Client ID"
                value={formData.config?.googleClientId || ''}
                onChange={(e) => handleConfigChange('googleClientId', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="googleClientSecret">Client Secret</Label>
              <Input
                id="googleClientSecret"
                type="password"
                placeholder="Your Google OAuth Client Secret"
                value={formData.config?.googleClientSecret || ''}
                onChange={(e) => handleConfigChange('googleClientSecret', e.target.value)}
              />
            </div>
          </div>
        );

      case 'azure':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="azureTenantId">Tenant ID</Label>
              <Input
                id="azureTenantId"
                placeholder="Your Azure AD Tenant ID"
                value={formData.config?.azureTenantId || ''}
                onChange={(e) => handleConfigChange('azureTenantId', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="azureClientId">Application (Client) ID</Label>
              <Input
                id="azureClientId"
                placeholder="Your Azure AD Application ID"
                value={formData.config?.azureClientId || ''}
                onChange={(e) => handleConfigChange('azureClientId', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="azureClientSecret">Client Secret</Label>
              <Input
                id="azureClientSecret"
                type="password"
                placeholder="Your Azure AD Client Secret"
                value={formData.config?.azureClientSecret || ''}
                onChange={(e) => handleConfigChange('azureClientSecret', e.target.value)}
              />
            </div>
          </div>
        );

      case 'ldap':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="ldapUrl">LDAP URL</Label>
              <Input
                id="ldapUrl"
                placeholder="ldap://your-ldap-server.com:389"
                value={formData.config?.ldapUrl || ''}
                onChange={(e) => handleConfigChange('ldapUrl', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ldapBindDn">Bind DN</Label>
              <Input
                id="ldapBindDn"
                placeholder="cn=admin,dc=example,dc=com"
                value={formData.config?.ldapBindDn || ''}
                onChange={(e) => handleConfigChange('ldapBindDn', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ldapBindPassword">Bind Password</Label>
              <Input
                id="ldapBindPassword"
                type="password"
                placeholder="LDAP bind password"
                value={formData.config?.ldapBindPassword || ''}
                onChange={(e) => handleConfigChange('ldapBindPassword', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="ldapBaseDn">Base DN</Label>
              <Input
                id="ldapBaseDn"
                placeholder="dc=example,dc=com"
                value={formData.config?.ldapBaseDn || ''}
                onChange={(e) => handleConfigChange('ldapBaseDn', e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="ldapUseTls"
                checked={formData.config?.ldapUseTls || false}
                onCheckedChange={(checked) => handleConfigChange('ldapUseTls', checked)}
              />
              <Label htmlFor="ldapUseTls">Use TLS</Label>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            Select a provider type to configure connection settings
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic">Basic Settings</TabsTrigger>
          <TabsTrigger value="connection">Connection</TabsTrigger>
          <TabsTrigger value="sync">Sync Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <GlassCard variant="default" hover={false}>
            <GlassCardHeader>
              <GlassCardTitle>Provider Information</GlassCardTitle>
              <GlassCardDescription>Configure basic provider details</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Acme Corp Google Workspace"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="type">Provider Type *</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select provider type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="google">Google Workspace</SelectItem>
                    <SelectItem value="azure">Azure Active Directory</SelectItem>
                    <SelectItem value="ldap">LDAP / Active Directory</SelectItem>
                    <SelectItem value="okta">Okta</SelectItem>
                    <SelectItem value="saml">SAML 2.0</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description for this provider"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>

        <TabsContent value="connection" className="space-y-4">
          <GlassCard variant="default" hover={false}>
            <GlassCardHeader>
              <GlassCardTitle>Connection Settings</GlassCardTitle>
              <GlassCardDescription>Configure authentication and connection details</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {renderProviderSpecificConfig()}
            </GlassCardContent>
          </GlassCard>
        </TabsContent>

        <TabsContent value="sync" className="space-y-4">
          <GlassCard variant="default" hover={false}>
            <GlassCardHeader>
              <GlassCardTitle>Sync Configuration</GlassCardTitle>
              <GlassCardDescription>Configure what data to sync and how often</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="syncUsers">Sync Users</Label>
                    <p className="text-sm text-muted-foreground">Import user accounts from this provider</p>
                  </div>
                  <Switch
                    id="syncUsers"
                    checked={formData.syncSettings?.syncUsers || false}
                    onCheckedChange={(checked) => handleSyncSettingChange('syncUsers', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="syncGroups">Sync Groups</Label>
                    <p className="text-sm text-muted-foreground">Import group memberships from this provider</p>
                  </div>
                  <Switch
                    id="syncGroups"
                    checked={formData.syncSettings?.syncGroups || false}
                    onCheckedChange={(checked) => handleSyncSettingChange('syncGroups', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enableAutoSync">Automatic Sync</Label>
                    <p className="text-sm text-muted-foreground">Enable scheduled automatic synchronization</p>
                  </div>
                  <Switch
                    id="enableAutoSync"
                    checked={formData.syncSettings?.enableAutoSync || false}
                    onCheckedChange={(checked) => handleSyncSettingChange('enableAutoSync', checked)}
                  />
                </div>
              </div>

              <Separator />

              <div>
                <Label htmlFor="syncInterval">Sync Interval (hours)</Label>
                <Select 
                  value={formData.syncSettings?.syncInterval?.toString() || '24'} 
                  onValueChange={(value) => handleSyncSettingChange('syncInterval', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every hour</SelectItem>
                    <SelectItem value="6">Every 6 hours</SelectItem>
                    <SelectItem value="12">Every 12 hours</SelectItem>
                    <SelectItem value="24">Daily</SelectItem>
                    <SelectItem value="168">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div>
                <Label>Attribute Mapping</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Map user attributes from your identity provider to local user fields
                </p>
                
                <div className="grid gap-3">
                  {[
                    { key: 'email', label: 'Email Address', required: true },
                    { key: 'firstName', label: 'First Name' },
                    { key: 'lastName', label: 'Last Name' },
                    { key: 'department', label: 'Department' },
                    { key: 'jobTitle', label: 'Job Title' },
                    { key: 'manager', label: 'Manager' }
                  ].map(({ key, label, required }) => (
                    <div key={key} className="grid grid-cols-2 gap-3 items-center">
                      <Label className="text-sm">
                        {label} {required && <Badge variant="secondary" className="ml-1 text-xs">Required</Badge>}
                      </Label>
                      <Input
                        placeholder={`Provider ${key} field`}
                        value={formData.syncSettings?.attributeMapping?.[key as keyof AttributeMapping] || ''}
                        onChange={(e) => handleAttributeMappingChange(key, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          {provider ? 'Update Provider' : 'Add Provider'}
        </Button>
      </div>
    </div>
  );
}